import getAllFiltered from "@api/graphql/generic/get-all-filtered";
import { downloadDeliveries } from "@api/Rest/download/dealsDownloadXLSX";
import { SelectInput } from "@components/common/inputs/SelectInput";
import { Box, Button, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { endOfAppDay, startOfAppDay } from "@repo/utils/date";
import { useQuery } from "@tanstack/react-query";
import type { PaginationState } from "@tanstack/react-table";
import type {
  DeepInclude,
  GetAllInput,
  KeyValuePair,
  OrderType,
  PrismaFilter,
  WhereFilter,
} from "@types_def/common/filter.types";
import type { QueryType } from "@types_def/common/query.types";
import type { Delivery } from "@types_def/models/delivery.types";
import { DeliveryStatus, type DeliveryType } from "@types_def/models/delivery.types";
import type Driver from "@types_def/models/driver.types";
import { deliveryTypesForUiFilter } from "@utils/constants/DeliveryType/deliveryTypeAttributes";
import { selectListAllOptions } from "@utils/constants/deliveryOptions";
import { getDeliveryAttributesByStatus } from "@utils/functions/Delivery/DeliveryStatus/delivery-status-attributes";
import { t } from "i18next";
import React, { useEffect, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { DataTable } from "@/components/ui/data-table";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";
import { toDeliveryListSortField } from "@/utils/delivery-list-sort-field";
import { fromPickerDate, toPickerDate } from "@/utils/picker-date";

import DriverQuickDetails from "../DriverQuickDetails";
import { useDriverDetails } from "../Querys";
import { DRIVER_HISTORY_COLUMNS } from "./columns";

type TableState = {
  filter?: WhereFilter<Delivery>;
  include?: DeepInclude<Delivery>;
  deliveriesTableQueryFields: QueryType<Delivery> | undefined;
};

export function DriverDeliveriesTable() {
  "use no memo";
  const [searchParams, setSearchParams] = useSearchParams();

  // Getting all filter from URL params
  const id = searchParams.get("id");
  const vin = searchParams.get("vin");
  const type = searchParams.get("type") as DeliveryType;
  const gte = searchParams.get("gte");
  const lte = searchParams.get("lte");
  const optionNameFilter = searchParams.get("optionNameFilter");
  const page = searchParams.get("page");

  const pageIndex = Number.parseInt(searchParams.get("page") ?? "0", 10) || 0;
  const orderByRaw = searchParams.get("orderBy");
  const orderBy =
    orderByRaw !== null && orderByRaw !== ""
      ? (toDeliveryListSortField(orderByRaw) as keyof Delivery)
      : "id";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";

  // State and hooks
  const location = useLocation();

  const urlRole = location.pathname.replace(/^\/([^/]*).*$/, "$1") as "admin" | "client";
  const { id: driverId } = useParams();
  const [tableState, setTableState] = React.useState<TableState>({
    filter: {
      selectedDriverId: Number.parseInt(driverId ?? "0", 10),
    },
    include: getDeliveryAttributesByStatus(DeliveryStatus.DELIVERED)[`${urlRole}DeliveryInclude`],
    deliveriesTableQueryFields: getDeliveryAttributesByStatus(DeliveryStatus.DELIVERED)[
      `${urlRole}DeliveriesTableQueryFields`
    ],
  });

  // Handlers
  const handleDateChange = (
    date: Date | undefined | null,
    prop: keyof PrismaFilter<Delivery, "carDeliveryDeadline">
  ) => {
    if (page) {
      searchParams.delete("page");
      setSearchParams(searchParams);
    }
    const instant = fromPickerDate(date);
    if (instant == null) {
      searchParams.delete(prop);
      setSearchParams(searchParams);
      return;
    }

    const nextDate = prop === "gte" ? startOfAppDay(instant) : endOfAppDay(instant);
    searchParams.set(prop, nextDate.toISOString());
    setSearchParams(searchParams);
  };

  const handleTypeChange = (nextType: DeliveryType) => {
    if (page) {
      searchParams.delete("page");
      setSearchParams(searchParams);
    }
    searchParams.set("type", nextType);
    setSearchParams(searchParams);
  };

  const handleTypeClear = () => {
    if (page) {
      searchParams.delete("page");
      setSearchParams(searchParams);
    }
    searchParams.delete("type");
    setSearchParams(searchParams);
    if (tableState.filter?.type) {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          type: undefined,
        },
      }));
    }
  };

  const handleOptionFilterChange = (value: string) => {
    if (page) {
      searchParams.delete("page");
      setSearchParams(searchParams);
    }
    if (value === undefined) {
      searchParams.delete("optionNameFilter");
      setSearchParams(searchParams);
      return;
    }
    searchParams.set("optionNameFilter", value);
    setSearchParams(searchParams);
  };

  // search by id
  useEffect(() => {
    if (id) {
      const deliveryID = id ? Number.parseInt(id, 10) : undefined;
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          id: deliveryID,
        },
      }));
      return;
    }

    setTableState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        id: undefined,
      },
    }));
  }, [id]);

  // search by vin
  useEffect(() => {
    if (vin) {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          vehicles: {
            some: {
              vin: {
                contains: vin,
              },
            },
          },
        },
      }));
      return;
    }

    setTableState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        vehicles: undefined,
      },
    }));
  }, [vin]);

  // search by type
  useEffect(() => {
    if (type) {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          type: {
            in: [type],
          },
        },
      }));
      return;
    }

    setTableState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        type: undefined,
      },
    }));
  }, [type]);

  // search by optionNameFilter
  useEffect(() => {
    if (optionNameFilter) {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          optionNameFilter,
        },
      }));
      return;
    }

    setTableState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        optionNameFilter: undefined,
      },
    }));
  }, [optionNameFilter]);

  // search by gte
  useEffect(() => {
    if (gte) {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          carDeliveryDeadline: {
            ...(prev.filter?.carDeliveryDeadline as object),
            gte,
          },
        },
      }));
      return;
    }

    setTableState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        carDeliveryDeadline: {
          ...(prev.filter?.carDeliveryDeadline as object),
          gte: undefined,
        },
      },
    }));
  }, [gte]);

  // search by lte
  useEffect(() => {
    if (lte) {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          carDeliveryDeadline: {
            ...(prev.filter?.carDeliveryDeadline as object),
            lte,
          },
        },
      }));
      return;
    }

    setTableState((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        carDeliveryDeadline: {
          ...(prev.filter?.carDeliveryDeadline as object),
          lte: undefined,
        },
      },
    }));
  }, [lte]);

  const typeFilter = (
    <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
      <Typography color="text.secondary" variant="body2">
        Type:
      </Typography>
      {deliveryTypesForUiFilter.map((deliveryType) => (
        <Button
          key={deliveryType}
          onClick={() => {
            if (type === deliveryType) {
              handleTypeClear();
              return;
            }
            handleTypeChange(deliveryType);
          }}
          size="small"
          variant={type === deliveryType ? "contained" : "outlined"}
        >
          {deliveryType}
        </Button>
      ))}
    </Stack>
  );

  const dateFilters = (
    <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
      <Typography color="text.secondary" variant="body2">
        Filtre periode:
      </Typography>
      <DatePicker
        label={t("common.shared.from")}
        onChange={(value) => handleDateChange(value ?? undefined, "gte")}
        slotProps={{
          textField: {
            size: "small",
          },
        }}
        value={toPickerDate(gte)}
      />
      <DatePicker
        label={t("common.shared.to")}
        onChange={(value) => handleDateChange(value ?? undefined, "lte")}
        slotProps={{
          textField: {
            size: "small",
          },
        }}
        value={toPickerDate(lte)}
      />
    </Stack>
  );

  const optionsSelectList = (
    <SelectInput<KeyValuePair>
      placeholder={t("common.shared.select")}
      label={t("layouts.sub-headers.filters.services")}
      optionTextProperty="value"
      optionValueProperty="key"
      value={`${tableState.filter?.optionNameFilter ?? ""}`}
      onChange={handleOptionFilterChange}
      options={selectListAllOptions.map((option) => ({
        ...option,
        key: option.key.toString(),
        value: t(`delivery.services.${option.key}`),
      }))}
      alignLabel
    />
  );
  const filters = [typeFilter, optionsSelectList, dateFilters];

  const { data: driverData } = useDriverDetails(Number(driverId));

  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "10", 10) || 10;

  const queryInput = useMemo<GetAllInput<Delivery, keyof Delivery>>(
    () => ({
      keyword: "",
      pagination: { page: pageIndex, perPage: pageSize },
      sort: { field: orderBy, order: order as OrderType },
      filter: tableState.filter,
      include: tableState.include,
    }),
    [pageIndex, pageSize, orderBy, order, tableState.filter, tableState.include]
  );

  const { data, isPending, isError } = useQuery({
    queryKey: ["driver-history-table", driverId, queryInput],
    queryFn: async () => {
      const response = await getAllFiltered<Delivery, keyof Delivery>(
        queryInput,
        "Delivery",
        tableState.deliveriesTableQueryFields!
      );
      return response.data;
    },
    enabled: Boolean(tableState.deliveriesTableQueryFields),
  });

  const pageCount = Math.ceil((data?.total ?? 0) / pageSize);

  const handlePaginationChange = (pagination: PaginationState) => {
    const nextParams = new URLSearchParams(searchParams);

    if (pagination.pageIndex > 0) {
      nextParams.set("page", `${pagination.pageIndex}`);
    } else {
      nextParams.delete("page");
    }

    if (pagination.pageSize !== 10) {
      nextParams.set("pageSize", String(pagination.pageSize));
    } else {
      nextParams.delete("pageSize");
    }

    setSearchParams(nextParams);
  };

  const handleSortingChange = (sorting: Array<{ id: string; desc: boolean }>) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSort = sorting[0];

    nextParams.delete("page");

    if (nextSort !== undefined) {
      nextParams.set("orderBy", nextSort.id);
      nextParams.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      nextParams.delete("orderBy");
      nextParams.delete("order");
    }

    setSearchParams(nextParams);
  };

  const { table } = useDataTable<Delivery>({
    data: data?.records ?? [],
    columns: DRIVER_HISTORY_COLUMNS,
    pageCount,
    manualPagination: true,
    manualSorting: true,
    externalPagination: { pageIndex, pageSize },
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
  });

  const handleExport = () => {
    downloadDeliveries({
      keyword: "",
      pagination: { page: pageIndex, perPage: pageSize },
      sort: { field: "id", order: order as OrderType },
      filter: tableState.filter,
      include: tableState.include,
    });
  };

  return (
    <>
      {driverData ? <DriverQuickDetails driver={driverData as unknown as Driver} /> : null}
      {Boolean(tableState.deliveriesTableQueryFields) && (
        <Box sx={{ p: 2 }}>
          <Stack gap={2}>
            <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              {filters.map((filterElement, index) => (
                <React.Fragment key={`driver-history-filter-${index}`}>
                  {filterElement}
                </React.Fragment>
              ))}
            </Stack>

            <DataTable isLoading={isPending} table={table} totalCount={data?.total ?? 0}>
              <DataTableToolbar table={table}>
                <Button onClick={handleExport} size="small" variant="outlined">
                  Export
                </Button>
              </DataTableToolbar>
            </DataTable>
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
              {isPending ? <Typography variant="body2">Loading...</Typography> : null}
              {isError ? (
                <Typography color="error" variant="body2">
                  Failed to load deliveries.
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      )}
    </>
  );
}
